import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Services = () => {
  const [serviceTypes, setServiceTypes] = useState([]);
  const [formData, setFormData] = useState({
    service_type: '',
    address: '',
    mobile: '',
    preferred_date: '',
    preferred_time: '',
    issue_description: ''
  });
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchServiceTypes();
  }, []);

  const fetchServiceTypes = async () => {
    try {
      const response = await axios.get(`${API}/service-types`);
      setServiceTypes(response.data.service_types);
    } catch (error) {
      console.error('Failed to fetch service types:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to book a service');
      navigate('/auth');
      return;
    }

    try {
      await axios.post(`${API}/services`, formData);
      toast.success('Service booking request submitted successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book service');
    }
  };

  return (
    <div className="min-h-screen py-12" data-testid="services-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-5xl md:text-6xl font-heading font-bold tracking-tight leading-none uppercase mb-4">
          BOOK A <span className="text-primary">SERVICE</span>
        </h1>
        <p className="text-lg text-slate-600 mb-8">Schedule a technician visit to your location</p>

        <Card className="p-8 border-slate-200">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="service-type">Service Type *</Label>
              <select
                id="service-type"
                value={formData.service_type}
                onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                required
                className="w-full mt-1 px-4 py-2 border border-slate-200 rounded-md bg-white"
                data-testid="service-type-select"
              >
                <option value="">Select Service Type</option>
                {serviceTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="address">Service Address *</Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Enter your complete address"
                required
                rows={3}
                className="mt-1"
                data-testid="address-input"
              />
            </div>

            <div>
              <Label htmlFor="mobile">Mobile Number *</Label>
              <Input
                id="mobile"
                type="tel"
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                placeholder="9876543210"
                required
                className="mt-1"
                data-testid="mobile-input"
              />
              <p className="text-xs text-slate-500 mt-1">Technician will call you for directions</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="preferred-date">Preferred Date *</Label>
                <Input
                  id="preferred-date"
                  type="date"
                  value={formData.preferred_date}
                  onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="date-input"
                />
              </div>
              <div>
                <Label htmlFor="preferred-time">Preferred Time *</Label>
                <Input
                  id="preferred-time"
                  type="time"
                  value={formData.preferred_time}
                  onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="time-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="issue-description">Issue Description *</Label>
              <Textarea
                id="issue-description"
                value={formData.issue_description}
                onChange={(e) => setFormData({ ...formData, issue_description: e.target.value })}
                placeholder="Describe the issue or service requirement"
                required
                rows={4}
                className="mt-1"
                data-testid="description-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-wider py-6 h-auto"
              data-testid="submit-booking-button"
            >
              Submit Booking Request
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Services;